import React, { useState } from "react";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User, Link, ExternalLink, Edit, MessageCircle, Play } from "lucide-react";
import { MentorSession } from "@/types/mentorSessions";
import { format, parseISO } from "date-fns";
import { ptBR } from 'date-fns/locale';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { updateSessionMeetingLink } from "@/services/mentorSessionsService";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { getOrCreateConversation } from "@/services/chat";
import { useNavigate } from "react-router-dom";

interface MentorSoldSessionCardProps {
  session: MentorSession;
  onActionCompleted?: () => void;
}

export function MentorSoldSessionCard({ session, onActionCompleted }: MentorSoldSessionCardProps) {
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [meetingLink, setMeetingLink] = useState(session.meeting_link || "");
  const [isUpdatingLink, setIsUpdatingLink] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Sincronizar o estado local quando session.meeting_link mudar (após recarregar)
  React.useEffect(() => {
    console.log(`🔄 [MentorSoldSessionCard] Sessão ${session.id} - meeting_link recebido:`, {
      meeting_link: session.meeting_link,
      hasMeetingLink: !!(session.meeting_link && session.meeting_link.trim() !== ''),
      currentState: meetingLink
    });
    
    if (session.meeting_link && session.meeting_link.trim() !== '') {
      setMeetingLink(session.meeting_link);
      console.log(`✅ [MentorSoldSessionCard] Estado atualizado com: "${session.meeting_link}"`);
    } else {
      setMeetingLink("");
      console.log(`⚠️ [MentorSoldSessionCard] meeting_link vazio ou null, resetando estado`);
    }
  }, [session.id, session.meeting_link]);

  const formatDate = (dateStr: string) => {
    try {
      if (!dateStr) return "Data não definida";
      const date = parseISO(dateStr + 'T00:00:00');
      return format(date, "dd 'de' MMMM", { locale: ptBR });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateStr || "Data inválida";
    }
  };

  const formatTime = (timeStr: string) => {
    try {
      if (!timeStr) return "Horário não definido";
      // Se já contém data completa, parse diretamente
      if (timeStr.includes('T')) {
        const date = parseISO(timeStr);
        return format(date, "HH:mm", { locale: ptBR });
      }
      // Se é apenas horário, adiciona data fictícia para parsing
      const date = parseISO(`2024-01-01T${timeStr}`);
      return format(date, "HH:mm", { locale: ptBR });
    } catch (e) {
      console.error("Error formatting time:", e);
      return timeStr || "Horário inválido";
    }
  };

  const calculateDuration = () => {
    try {
      if (!session.scheduled_start || !session.scheduled_end) return "Duração não definida";
      
      let startTime, endTime;
      
      // Parse start time
      if (session.scheduled_start.includes('T')) {
        startTime = parseISO(session.scheduled_start);
      } else {
        startTime = parseISO(`2024-01-01T${session.scheduled_start}`);
      }
      
      // Parse end time
      if (session.scheduled_end.includes('T')) {
        endTime = parseISO(session.scheduled_end);
      } else {
        endTime = parseISO(`2024-01-01T${session.scheduled_end}`);
      }
      
      const diffMs = endTime.getTime() - startTime.getTime();
      const diffMinutes = Math.round(diffMs / (1000 * 60));
      
      if (diffMinutes < 60) {
        return `${diffMinutes} min`;
      } else {
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
      }
    } catch (e) {
      console.error("Error calculating duration:", e);
      return "Duração inválida";
    }
  };

  const getBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmado':
        return 'default';
      case 'concluido':
        return 'secondary';
      case 'pendente':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const formatStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmado':
        return 'Confirmado';
      case 'concluido':
        return 'Concluído';
      case 'pendente':
        return 'Pendente';
      default:
        return status;
    }
  };

  const handleSaveLink = async () => {
    const trimmedLink = meetingLink?.trim() || '';
    
    if (!trimmedLink) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Por favor, insira um link válido"
      });
      return;
    }

    setIsUpdatingLink(true);
    try {
      console.log(`💾 [MentorSoldSessionCard] Salvando link: "${trimmedLink}"`);
      await updateSessionMeetingLink(session.id, trimmedLink);
      
      // Atualizar o estado local imediatamente após salvar
      setMeetingLink(trimmedLink);
      setIsEditingLink(false);
      
      console.log(`✅ [MentorSoldSessionCard] Link salvo e estado atualizado: "${trimmedLink}"`);
      
      toast({
        title: "Link atualizado!",
        description: "O link da reunião foi salvo com sucesso"
      });
      
      // Chamar onActionCompleted para recarregar os dados
      onActionCompleted?.();
    } catch (error) {
      console.error("❌ [MentorSoldSessionCard] Error updating meeting link:", error);
      toast({
        variant: "destructive",
        title: "Erro ao salvar",
        description: error instanceof Error ? error.message : "Não foi possível salvar o link da reunião"
      });
    } finally {
      setIsUpdatingLink(false);
    }
  };

  const handleStartChat = async () => {
    try {
      setIsCreatingConversation(true);
      
      console.log(`🚀 [MentorSoldSessionCard] Criando/buscando conversa com jogador ${session.player_profile_id}`);
      
      // Criar ou obter conversa existente
      const conversationId = await getOrCreateConversation(session.player_profile_id);
      
      console.log(`✅ [MentorSoldSessionCard] Conversa criada/encontrada: ${conversationId}`);
      
      // Redirecionar para a página de chat do painel do mentor com a conversa específica
      navigate(`/painel-mentor/chat/${conversationId}`);
      
    } catch (error) {
      console.error("❌ [MentorSoldSessionCard] Erro ao criar conversa:", error);
      toast({
        title: "Erro ao abrir chat",
        description: "Não foi possível abrir o chat. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return (cents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  return (
    <Card className="bg-[#1E293B] border-[#2A3749] hover:border-[#00B6F0]/30 transition-colors">
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Banner da Mentoria */}
          <div className="flex-shrink-0">
            <div className="w-full lg:w-32 h-20 lg:h-24 rounded-lg overflow-hidden bg-gradient-to-br from-[#00B6F0]/20 to-[#7C3AED]/20 flex items-center justify-center">
              {session.mentorship_cover_url ? (
                <img 
                  src={session.mentorship_cover_url} 
                  alt={session.mentorship_title || "Banner da mentoria"} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <Play className="h-8 w-8 text-[#00B6F0]" />
              )}
            </div>
          </div>

          {/* Informações Principais */}
          <div className="flex-grow space-y-3">
            {/* Header com Título e Status */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-white line-clamp-1">
                  {session.mentorship_title || `Mentoria de ${session.game_name}`}
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <User className="h-4 w-4" />
                  <span>com {session.player_name}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge 
                  variant={getBadgeVariant(session.status)}
                  className="text-xs"
                >
                  {formatStatus(session.status)}
                </Badge>
                {session.is_paid && (
                  <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-400 border-green-500/20">
                    Pago
                  </Badge>
                )}
              </div>
            </div>

            {/* Informações de Data e Horário */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <Calendar className="h-4 w-4 text-[#00B6F0]" />
                <span>{formatDate(session.scheduled_date)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Clock className="h-4 w-4 text-[#00B6F0]" />
                <span>{formatTime(session.scheduled_start)} - {formatTime(session.scheduled_end)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <span className="text-[#00B6F0] font-medium">Duração:</span>
                <span>{calculateDuration()}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <span className="text-[#00B6F0] font-medium">Valor:</span>
                <span className="font-semibold text-green-400">{formatCurrency(session.price_cents)}</span>
              </div>
            </div>

            {/* Informações do Jogador */}
            <div className="flex items-center gap-3 p-3 bg-[#0F172A] rounded-lg">
              <Avatar className="h-10 w-10">
                <AvatarImage src={session.player_image_url || undefined} />
                <AvatarFallback className="bg-[#00B6F0] text-black font-semibold">
                  {session.player_name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-grow">
                <div className="font-medium text-white">{session.player_name}</div>
                <div className="text-sm text-gray-400">{session.game_name}</div>
              </div>
            </div>

            {/* Ações e Link da Reunião */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <div className="flex-grow">
                {isEditingLink ? (
                  <div className="flex gap-2">
                    <Input
                      value={meetingLink}
                      onChange={(e) => setMeetingLink(e.target.value)}
                      placeholder="Cole o link da reunião aqui..."
                      className="flex-grow bg-[#0F172A] border-[#2A3749] text-white"
                    />
                    <Button
                      onClick={handleSaveLink}
                      disabled={isUpdatingLink}
                      size="sm"
                      className="bg-[#00B6F0] text-black hover:bg-[#00A3D9]"
                    >
                      {isUpdatingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
                    </Button>
                    <Button
                      onClick={() => setIsEditingLink(false)}
                      variant="outline"
                      size="sm"
                      className="border-[#2A3749] text-white hover:bg-[#2A3749]"
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {session.meeting_link ? (
                      <>
                        <Button
                          onClick={() => window.open(session.meeting_link, '_blank')}
                          variant="outline"
                          size="sm"
                          className="flex-grow sm:flex-grow-0 border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0] hover:text-black"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Abrir Reunião
                        </Button>
                        <Button
                          onClick={() => setIsEditingLink(true)}
                          variant="outline"
                          size="sm"
                          className="border-[#2A3749] text-white hover:bg-[#2A3749]"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => setIsEditingLink(true)}
                        variant="outline"
                        size="sm"
                        className="flex-grow sm:flex-grow-0 border-[#2A3749] text-white hover:bg-[#2A3749]"
                      >
                        <Link className="h-4 w-4 mr-2" />
                        Adicionar Link
                      </Button>
                    )}
                  </div>
                )}
              </div>
              
              <Button
                onClick={handleStartChat}
                disabled={isCreatingConversation}
                variant="outline"
                size="sm"
                className="border-[#2A3749] text-white hover:bg-[#2A3749]"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                {isCreatingConversation ? "Abrindo chat..." : "Falar com Jogador"}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 